import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import Lenis from "lenis";
import "@/App.css";
import Nav from "@/components/Nav";
import {
  StorySection,
  ProductsSection,
  ChickenOilSection,
  PetSection,
  TradeSection,
  ShopSection,
  ContactSection,
  scrollToId,
} from "@/components/Sections";

const EASE = [0.76, 0, 0.24, 1];

const HeroBtn = ({ children, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="cursor-pointer rounded-full border border-[#1c2b1e]/50 bg-transparent px-6 py-2.5 font-display text-[11px] font-semibold uppercase tracking-[0.22em] text-[#1c2b1e] transition-colors duration-300 hover:bg-[#1c2b1e] hover:text-[#f1e7d5] sm:px-8 sm:py-3 sm:text-xs"
  >
    {children}
  </button>
);

function App() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 20 });
  const sy = useSpring(my, { stiffness: 40, damping: 20 });
  const imgX = useTransform(sx, [-0.5, 0.5], [14, -14]);
  const imgY = useTransform(sy, [-0.5, 0.5], [10, -10]);
  const frame = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const onMove = (e) => {
    const r = frame.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <main data-testid="site-root" className="bg-[#151009]">
      <Nav />
      <section
        ref={frame}
        onMouseMove={onMove}
        data-testid="hero-page"
        className="relative h-[100svh] w-full overflow-hidden"
      >
        <motion.div
          className="absolute inset-0"
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1.8, ease: EASE, delay: 0.3 }}
        >
          <motion.div
            className="absolute inset-0"
            initial={{ scale: 1.08, opacity: 0.6 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 2.6, ease: EASE, delay: 0.3 }}
          >
            <motion.div style={{ x: imgX, y: imgY }} className="absolute inset-0">
              <img
                src="/hero-bg.jpg"
                alt=""
                data-testid="hero-image"
                className="hero-layer hero-bg"
                draggable={false}
              />
              <div className="hero-chicken-shrink absolute inset-0">
                <motion.img
                  src="/hero-chicken.png"
                  alt=""
                  data-testid="hero-chicken"
                  className="hero-layer hero-fg"
                  draggable={false}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 1.2 }}
                />
              </div>
              <motion.img
                src="/hero-logo.png"
                alt="The Chicken Whisperer"
                data-testid="hero-logo"
                className="hero-layer hero-fg"
                draggable={false}
                initial={{ opacity: 0, y: -24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 1.5 }}
              />
            </motion.div>
          </motion.div>
        </motion.div>

        <div className="vignette pointer-events-none absolute inset-0" />
        <div className="grain pointer-events-none absolute inset-0" />

        <motion.div
          className="pointer-events-none absolute inset-x-0 top-0 h-px origin-left bg-[#c9a45c]"
          initial={{ scaleX: 0, opacity: 0.9 }}
          animate={{ scaleX: 1, opacity: 0 }}
          transition={{ duration: 2.4, ease: EASE, delay: 0.9 }}
        />

        <motion.div
          data-testid="hero-taglines"
          className="absolute inset-x-4 bottom-[18%] z-10 flex flex-col items-center gap-3 text-center sm:gap-4"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 1.9 }}
        >
          <p
            data-testid="hero-tagline-main"
            className="mx-auto max-w-5xl font-display font-black uppercase leading-relaxed tracking-[0.18em] text-[#22301f]"
            style={{ fontSize: "clamp(14px, 1.7vw, 26px)" }}
          >
            Original foods made with British chicken.
            <br />
            Founded on principle.
          </p>
          <p
            data-testid="hero-tagline-sub"
            className="font-display font-bold uppercase tracking-[0.18em] text-[#e6c27a] [text-shadow:0_1px_8px_rgba(10,13,9,0.7)] md:tracking-[0.3em] md:text-[#C89D3C] md:[text-shadow:1px_0_3px_rgba(10,13,9,0.8),-1px_0_3px_rgba(10,13,9,0.8),0_1px_3px_rgba(10,13,9,0.8),0_-1px_3px_rgba(10,13,9,0.8),0_3px_20px_rgba(10,13,9,0.9)]"
            style={{ fontSize: "clamp(14px, 2.1vw, 36px)", marginTop: "clamp(14px, 3.5vh, 60px)" }}
          >
            Trade. Retail. Private Label.
          </p>
        </motion.div>

      </section>

      <motion.div
        data-testid="hero-cta"
        className="flex flex-wrap items-center justify-center gap-3 bg-[#f1e7d5] px-6 py-8 sm:gap-5 sm:px-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 2.2 }}
      >
        <HeroBtn onClick={() => scrollToId("chicken-oil")} testId="hero-discover-btn">
          Discover Our Pure Chicken Oil
        </HeroBtn>
        <HeroBtn onClick={() => scrollToId("trade")} testId="hero-trade-btn">
          Trade Enquiries
        </HeroBtn>
      </motion.div>

      <StorySection />
      <ProductsSection />
      <ChickenOilSection />
      <PetSection />
      <TradeSection />
      <ShopSection />
      <ContactSection />
    </main>
  );
}

export default App;
